import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { pinTrip, editPin, pinHotel, editHotelPin, getSuggestedCurrency, getSupportedCurrencies, getPins, getTierLimits } from '../api/atlast';
import { supabase, getLocalUser } from '../supabase';
import PageBackground from './PageBackground';
import LimitModal from './LimitModal';
import PinBundlePurchaseModal from './PinBundlePurchaseModal';
import PurchaseSuccessModal from './PurchaseSuccessModal';
import { theme } from '../theme';
import AppNav from '../AppNav';
import AirportAutocomplete from '../AirportAutocomplete';

const MAX_TOTAL_PASSENGERS = 9;
const MAX_HOTEL_GUESTS = 8;
const MAX_HOTEL_ROOMS = 5;

// A one-time SUGGESTED default for new pins, guessed from the browser's
// locale — never used to reinterpret a currency the user already chose.
// Deliberately a curated list of common markets, not all ~170 ISO 4217
// codes — this is a starting-point guess the user can always override
// via the dropdown below, not an exhaustive currency picker.
const REGION_TO_CURRENCY = {
  CA: 'CAD', US: 'USD', GB: 'GBP', AU: 'AUD', NZ: 'NZD',
  PK: 'PKR', IN: 'INR', DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR',
  NL: 'EUR', IE: 'EUR', PT: 'EUR', AT: 'EUR', BE: 'EUR', FI: 'EUR',
  JP: 'JPY', CN: 'CNY', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK',
  MX: 'MXN', BR: 'BRL', AE: 'AED', SA: 'SAR', SG: 'SGD', HK: 'HKD',
  ZA: 'ZAR', PH: 'PHP', ID: 'IDR', MY: 'MYR', TH: 'THB', KR: 'KRW',
  NG: 'NGN', BD: 'BDT', VN: 'VND', EG: 'EGP', KE: 'KES', QA: 'QAR',
  // Added (Sep 15, per Ali's report — real gap, caught live: picking
  // Sheremetyevo (Moscow, Russia) as a destination showed no currency
  // suggestion at all, since RU simply wasn't in this list — nothing
  // to do with Frankfurter or the FX pipeline, this map is a
  // completely separate, static lookup). Same "large population or
  // real travel volume" bar as the Aug 18 expansion above.
  RU: 'RUB', TR: 'TRY', PL: 'PLN', IL: 'ILS', CO: 'COP', AR: 'ARS',
  CL: 'CLP', PE: 'PEN', UA: 'UAH', CZ: 'CZK', HU: 'HUF', RO: 'RON',
  MA: 'MAD', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD', LK: 'LKR',
  NP: 'NPR', TW: 'TWD', IS: 'ISK'
};

// Used only if the live /geo/supported-currencies fetch fails — a
// small, safe set guaranteed to work, not the full dropdown anymore.
// Expanded (Aug 18, per Ali's report — a real gap, not a naming
// cleanup) to add NGN/BDT/VND/EGP/KES, matching the same addition on
// the backend's CURATED_CURRENCIES — these represent real, large
// populations (Nigeria, Bangladesh, Vietnam, Egypt, Kenya) that were
// previously missing from both this list and Frankfurter's, despite
// the FX conversion pipeline already being able to reach them via its
// own fallback provider.
const FALLBACK_CURRENCIES = [
  'AED', 'AUD', 'BDT', 'BRL', 'CAD', 'CHF', 'CNY', 'DKK', 'EGP', 'EUR',
  'GBP', 'HKD', 'IDR', 'INR', 'JPY', 'KES', 'KRW', 'MXN', 'MYR', 'NGN',
  'NOK', 'NZD', 'PHP', 'PKR', 'QAR', 'SAR', 'SEK', 'SGD', 'THB', 'USD', 'VND', 'ZAR'
];

function guessDefaultCurrency() {
  try {
    // navigator.language is typically "en-CA", "ur-PK", "en-GB", etc. —
    // the region subtag after the hyphen maps reasonably well to a
    // likely currency. Falls back to CAD (FlyAtlast's home market) if
    // the locale has no region subtag or isn't in our curated list —
    // this is only ever a starting suggestion, never forced.
    const locale = navigator.language || navigator.languages?.[0] || '';
    const region = locale.split('-')[1]?.toUpperCase();
    return REGION_TO_CURRENCY[region] || 'CAD';
  } catch {
    return 'CAD';
  }
}

function PinTrip() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill;
  // Trip carried over from the website demo (see utils/demoHandoff.js). Kept
  // separate from `prefill` on purpose: `prefill` means "editing an existing
  // pin" and changes the button text and warnings.
  const demoTrip = location.state?.demoTrip;

  // Which pin type this form is creating/editing. Defaults to 'flight'
  // for a brand-new pin. When editing, Dashboard.js is expected to pass
  // prefill.pin_type ('flight' or 'hotel') alongside the pin data, so an
  // edit always reopens on the correct side of the toggle rather than
  // defaulting back to flight.
  const [pinType, setPinType] = useState(prefill?.pin_type || 'flight');

  // New (Aug 31, per Ali's report — Phase A of the pin-purchase build):
  // every submit handler already fetches the current user locally when
  // it actually needs it for the API call, but PinBundlePurchaseModal
  // needs a userId available at render time, before any submit has
  // happened — hence this one component-level fetch on mount, separate
  // from (and not replacing) the per-submit fetches elsewhere in this
  // file.
  const [userId, setUserId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getLocalUser().then(({ data: { user } }) => {
      if (!cancelled && user) setUserId(user.id);
    });
    return () => { cancelled = true; };
  }, []);

  // Early, non-blocking limit notice (Sep 3, per Ali's report — real
  // UX gap found live from a screenshot). Before this, someone at
  // their pin limit only found out after filling in a whole real trip
  // and clicking "Pin This Trip" — the PIN_LIMIT_REACHED block further
  // down. Withholding a known constraint until someone's already
  // invested effort is a manipulative pattern even when it "works" —
  // this surfaces the same information honestly, up front, without
  // preventing them from filling the form out anyway if they want to
  // decide at the end. Only runs for a brand-new pin (prefill means
  // editing an existing one, which was never blocked by this cap).
  const [earlyLimitNotice, setEarlyLimitNotice] = useState(null);
  // Upfront modal (Sep 14, per Ali's report — real confusion, caught
  // live: the passive inline banner below was easy to miss entirely,
  // so someone at their pin cap could fill out the whole form and
  // only discover they're blocked at the very end, on submit. This
  // shows the same information as an actual blocking dialog the
  // moment the form opens, using the same LimitModal/purchase flow
  // Dashboard.js already uses elsewhere, rather than a new pattern.
  // Dismissing it ("Not now") still lets them fill out the form —
  // matches the existing "you can still fill this out and decide at
  // the end" philosophy, just makes the choice itself unmissable
  // instead of easy to scroll past.
  const [showEarlyLimitModal, setShowEarlyLimitModal] = useState(false);
  useEffect(() => {
    if (!userId || prefill) return;
    let cancelled = false;
    Promise.all([getPins(userId), getTierLimits(userId)]).then(([pins, limits]) => {
      if (!cancelled && pins.length >= limits.max_active_pins) {
        setEarlyLimitNotice(limits.max_active_pins);
        setShowEarlyLimitModal(true);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, prefill]);

  const [loading, setLoading] = useState(false);
  // Limit-reached modal (Aug 19, per Ali's report — item 1 of the
  // parked-items list). Mirrors Dashboard.js's upgradePrompt pattern
  // exactly, using the same shared LimitModal component rather than a
  // second local copy of the same UI.
  const [limitMessage, setLimitMessage] = useState(null);
  // Added (Sep 18, per Ali's report — real point: this whole date-
  // validation block used three raw browser alert() calls, breaking
  // the same visual consistency LimitModal already fixed for every
  // other prompt in this app). Reuses LimitModal rather than a new
  // component — same pattern, no new UI to build or maintain.
  const [dateValidationError, setDateValidationError] = useState(null);
  // Added (Sep 20) — the remaining raw alert()/confirm() cleanup pass.
  // Same LimitModal-reuse pattern as dateValidationError right above,
  // for every other pre-submit field check (missing origin/destination,
  // invalid budget, passenger caps, hotel room/guest counts) that isn't
  // specifically about a date. Kept as its own state rather than
  // folded into dateValidationError so the modal's title stays
  // accurate to what's actually wrong ("Check your details" vs "Check
  // your dates"), same reasoning as the Sep 19 title fix on
  // limitMessage below.
  const [fieldValidationError, setFieldValidationError] = useState(null);
  // Real action added (Aug 31, per Ali's report — Phase A of the pin-
  // purchase build): when the limit hit was specifically the active-
  // pin cap (detected via the backend's PIN_LIMIT_REACHED prefix, see
  // routes/trips.py), the modal now offers a real "buy 3 more pins"
  // path instead of just an acknowledgment. Other limit errors
  // (passenger count, etc.) still show the plain dismiss-only version.
  const [isPinLimitError, setIsPinLimitError] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  // Starts with the small safe fallback so the dropdown is never empty
  // while the real, live list is being fetched — replaced the moment
  // that fetch resolves (or left as-is if it fails).
  const [currencyOptions, setCurrencyOptions] = useState(FALLBACK_CURRENCIES);

  useEffect(() => {
    getSupportedCurrencies().then(codes => {
      if (codes && codes.length > 0) setCurrencyOptions(codes);
    });
  }, []);

  // --- Flight fields ---
  const [form, setForm] = useState({
    destination: prefill?.destination || '',
    destination_code: prefill?.destination_code || '',
    // display labels feed AirportAutocomplete's own value prop (Aug 31,
    // per Ali's report — Phase 4 autocomplete rebuild) — built from
    // prefill data when editing/duplicating an existing pin, so the
    // field shows a real "City (CODE)" label immediately rather than
    // looking blank despite destination_code already being set.
    destination_display: prefill?.destination && prefill?.destination_code
      ? `${prefill.destination} (${prefill.destination_code})`
      : '',
    origin_code: prefill?.origin_code || '',
    // Starts genuinely blank now (Sep 7, per external review feedback
    // — real, fair complaint: a silently pre-filled "Toronto" looked
    // like an assumption about the user rather than a neutral default,
    // and it wasn't obvious at a glance that it was editable rather
    // than fixed). Editing an existing pin still correctly shows its
    // real origin immediately via prefill.
    origin_display: prefill?.origin_code || '',
    // country codes for the destination-based currency auto-suggestion
    // (Sep 15, per Ali's report) — null on a fresh form or when editing
    // an existing pin (no re-selection happens then, so no country
    // code is known); populated only once AirportAutocomplete's own
    // onSelect actually fires for that field.
    origin_country_code: null,
    destination_country_code: null,
    budget: prefill?.budget ? String(prefill.budget) : '',
    // Editing an existing pin keeps its already-chosen currency; a brand
    // new pin gets a locale-guessed suggestion, never a hardcoded 'CAD'.
    currency: prefill?.currency || guessDefaultCurrency(),
    // Prefilled from the existing pin when editing (Sep 16, per Ali's
    // report — real bug, caught live: clicking Edit to raise a budget
    // lost the departure/return dates entirely, while everything else
    // — airports, passengers, budget — carried over correctly. The
    // original reasoning here was written for a "duplicate this pin"
    // scenario where fresh dates would make sense — but that's not
    // actually a reachable flow anywhere in this app; prefill is only
    // ever set by handleEditPin, always with the pin's real id, always
    // meaning a genuine edit of that exact pin, never a duplicate-to-
    // new. For a real edit, keeping the dates is clearly the right
    // default — someone raising a budget by $50 shouldn't have to
    // remember and retype dates they never meant to change.
    departure_date: prefill?.departure_date || '',
    return_date: prefill?.return_date || '',
    date_flexible: prefill?.date_flexible || false,
    adults: prefill?.adults || 1,
    // Locked forever once a pin is created — the backend's edit_pin
    // ignores whatever this holds and carries the original pin's value
    // forward regardless, but defaulting it correctly here still
    // matters so a fresh pin's very first submission is right.
    flight_preference: prefill?.flight_preference || 'best_value'
  });
  const [childAges, setChildAges] = useState(
    prefill?.child_ages && prefill.child_ages.length > 0
      ? prefill.child_ages.map(age => String(age))
      : []
  );

  // Explicit trip-type toggle, not an implicit "leave the date blank"
  // convention — a real user reported not realizing one-way was even
  // possible when the only signal was a parenthetical on the label.
  // Defaults to one-way only when editing a pin that was actually saved
  // without a return date; a brand-new pin always starts as round-trip.
  const [isOneWay, setIsOneWay] = useState(!!prefill && !prefill?.return_date);

  // --- Hotel fields — separate state, mirrors hotel_pins' real columns
  // (location, check_in_date, check_out_date, num_rooms, num_guests,
  // budget). Dates left blank on edit for the same reason as flights above. ---
  const [hotelForm, setHotelForm] = useState({
    location: prefill?.pin_type === 'hotel' ? (prefill?.location || '') : '',
    check_in_date: '',
    check_out_date: '',
    num_rooms: prefill?.pin_type === 'hotel' ? (prefill?.num_rooms || 1) : 1,
    num_guests: prefill?.pin_type === 'hotel' ? (prefill?.num_guests || 2) : 2,
    budget: prefill?.pin_type === 'hotel' ? String(prefill?.budget || '') : '',
    currency: (prefill?.pin_type === 'hotel' && prefill?.currency) || guessDefaultCurrency()
  });

  const today = new Date().toISOString().split('T')[0];
  const totalPassengers = parseInt(form.adults || 0, 10) + childAges.length;

  // stepDestinationDone/stepBudgetDone/stepDatesDone/stepsDone removed
  // (Aug 17, per Ali's report) — this whole cluster only ever fed the
  // progress bar, itself removed for being a static visual that never
  // actually tracked real form state.

  // Switching to one-way clears any already-entered return date rather
  // than just hiding the field — leaving stale data in a hidden field
  // risks it silently being submitted if something re-shows the field
  // later (e.g. switching back and forth) without the user realizing
  // the old date was still sitting there.
  const handleTripTypeChange = (oneWay) => {
    setIsOneWay(oneWay);
    if (oneWay) setForm(prev => ({ ...prev, return_date: '' }));
  };

  const addAdult = () => {
    if (totalPassengers >= MAX_TOTAL_PASSENGERS) {
      setFieldValidationError(`Maximum ${MAX_TOTAL_PASSENGERS} passengers per trip.`);
      return;
    }
    setForm(prev => ({ ...prev, adults: parseInt(prev.adults || 0, 10) + 1 }));
  };

  const removeAdult = () => {
    setForm(prev => ({ ...prev, adults: Math.max(1, parseInt(prev.adults || 1, 10) - 1) }));
  };

  // Tracks whether the user has manually picked a currency this
  // session — once true, the IP-based suggestion below must never
  // overwrite their choice. A ref, not state: the geo lookup is an
  // async network call whose .then() closure would otherwise only ever
  // see the value captured at mount (always false) rather than
  // whatever it's actually become by the time the response arrives.
  const currencyManuallySetRef = React.useRef(false);

  // One-time IP-based currency suggestion, only for a brand-new pin —
  // an edit already has a real saved currency from prefill and should
  // never have that silently replaced by a fresh guess. This upgrades
  // the instant browser-locale guess already set in useState above
  // (which stays as the fallback if this lookup fails, is disabled via
  // missing API key, or simply hasn't resolved yet by the time the user
  // submits) with a likely-more-accurate one based on actual location.
  useEffect(() => {
    if (prefill) return;
    getSuggestedCurrency().then(result => {
      if (result.currency && !currencyManuallySetRef.current) {
        setForm(prev => ({ ...prev, currency: result.currency }));
        setHotelForm(prev => ({ ...prev, currency: result.currency }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill from the website demo, once, on arrival. Marks the currency as
  // manually set so the IP-based suggestion above can't overwrite it.
  useEffect(() => {
    if (!demoTrip) return;
    currencyManuallySetRef.current = true;
    setForm(prev => ({
      ...prev,
      destination: demoTrip.destination_name,
      destination_code: demoTrip.destination_code,
      destination_display: demoTrip.destination_display,
      destination_country_code: demoTrip.destination_country_code,
      origin_code: demoTrip.origin_code,
      origin_display: demoTrip.origin_display,
      origin_country_code: demoTrip.origin_country_code,
      departure_date: demoTrip.departure_date,
      return_date: demoTrip.return_date,
      budget: String(demoTrip.budget),
      currency: demoTrip.currency,
      adults: demoTrip.adults || 1,
    }));
    setChildAges((demoTrip.child_ages || []).map(age => String(age)));
    setIsOneWay(!demoTrip.return_date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // normalizeDateValue added (Sep 19, per Ali's report — real,
  // live-caught bug: a native type="date" input is supposed to
  // always hand back true ISO (YYYY-MM-DD) in .value per the HTML
  // spec, regardless of the picker's own visual display format — but
  // Ali got "Date must be in YYYY-MM-DD format" from the backend
  // despite using the picker itself, not typing. handleChange stores
  // e.target.value completely raw with no transformation of its own,
  // so this points at the platform's own date input occasionally
  // handing back a locale-formatted string (MM/DD/YYYY) instead —
  // a known category of quirk in some mobile WebView date pickers.
  // Can't fully confirm the exact cause without the actual device,
  // but this fixes it regardless of why it's happening: detect the
  // one alternate format that would actually explain this exact
  // error, convert it, and leave anything else untouched (the
  // backend's own validator is still the real safety net either
  // way).
  const normalizeDateValue = (value) => {
    if (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const mmddyyyy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      const [, mm, dd, yyyy] = mmddyyyy;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'currency') currencyManuallySetRef.current = true;
    // Airport codes are always 3 uppercase letters (IATA standard) — force
    // this on input rather than only at submit time, so what the user
    // sees in the field matches what actually gets saved, and so mixed-
    // case codes (the Karachi bug) can't reach the backend at all.
    const finalValue = (name === 'destination_code' || name === 'origin_code')
      ? value.toUpperCase()
      : (name === 'departure_date' || name === 'return_date')
        ? normalizeDateValue(value)
        : (type === 'checkbox' ? checked : value);
    setForm({ ...form, [name]: finalValue });
  };

  const handleHotelChange = (e) => {
    const { name, value } = e.target;
    if (name === 'currency') currencyManuallySetRef.current = true;
    // Same normalization as the flight form's handleChange (Sep 19,
    // per Ali's report) — same class of date input, same potential
    // platform quirk, so same defensive fix.
    const finalValue = (name === 'check_in_date' || name === 'check_out_date') ? normalizeDateValue(value) : value;
    setHotelForm({ ...hotelForm, [name]: finalValue });
  };

  const addChild = () => {
    if (totalPassengers >= MAX_TOTAL_PASSENGERS) {
      setFieldValidationError(`Maximum ${MAX_TOTAL_PASSENGERS} passengers per trip.`);
      return;
    }
    setChildAges([...childAges, '']);
  };

  const updateChildAge = (index, value) => {
    const updated = [...childAges];
    updated[index] = value;
    setChildAges(updated);
  };

  const removeChild = (index) => {
    setChildAges(childAges.filter((_, i) => i !== index));
  };

  const handleSubmitFlight = async () => {
    // Added alongside the Toronto-default removal (Sep 7) — previously
    // origin_code silently fell back to 'YYZ', so an empty origin could
    // never actually reach this point. Now that it genuinely starts
    // blank, this is a real, necessary check, not a redundant one — a
    // user submitting immediately without touching a field would
    // otherwise send an empty origin_code straight to the backend.
    if (!form.origin_code) {
      setFieldValidationError('Please select a departure airport.');
      return;
    }

    if (!form.destination_code) {
      setFieldValidationError('Please select a destination.');
      return;
    }

    const budgetValue = parseFloat(form.budget);
    if (!form.budget || isNaN(budgetValue) || budgetValue <= 0) {
      setFieldValidationError('Please enter a valid budget.');
      return;
    }

    if (!form.departure_date) {
      setDateValidationError('Please select a departure date.');
      return;
    }

    if (form.departure_date < today) {
      setDateValidationError('Departure date must be in the future.');
      return;
    }

    if (form.return_date && form.return_date <= form.departure_date) {
      setDateValidationError('Return date must be after your departure date.');
      return;
    }

    const adultsValue = parseInt(form.adults, 10);
    if (!adultsValue || adultsValue < 1) {
      setFieldValidationError('At least 1 adult is required.');
      return;
    }

    // Validate each child age is filled in and within 0-17 before submit —
    // backend re-validates this too, but catching it here avoids a round
    // trip for an obvious mistake.
    const parsedChildAges = [];
    for (const age of childAges) {
      const parsedAge = parseInt(age, 10);
      if (age === '' || isNaN(parsedAge) || parsedAge < 0 || parsedAge > 17) {
        setFieldValidationError('Please enter a valid age (0-17) for each child, or remove the row.');
        return;
      }
      parsedChildAges.push(parsedAge);
    }

    if (adultsValue + parsedChildAges.length > MAX_TOTAL_PASSENGERS) {
      setFieldValidationError(`Maximum ${MAX_TOTAL_PASSENGERS} passengers per trip.`);
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await getLocalUser();

      // destination_display/origin_display are UI-only helper fields
      // for AirportAutocomplete's own display label (Aug 31, per Ali's
      // report) — never part of the actual pin schema, so explicitly
      // excluded from the spread here rather than relying on the
      // backend to silently ignore unknown fields.
      const { destination_display, origin_display, ...formForSubmit } = form;
      const tripData = {
        ...formForSubmit,
        // Belt-and-suspenders uppercase, since handleChange already does
        // this live — but an edit's prefill data (form.destination_code
        // set directly from a prior pin, not typed) never passes through
        // handleChange, so this catches that path too.
        destination_code: form.destination_code.toUpperCase(),
        origin_code: form.origin_code.toUpperCase(),
        budget: budgetValue,
        adults: adultsValue,
        child_ages: parsedChildAges,
        // One-way trips have no return date: send null, never an empty string.
        return_date: form.return_date || null,
        user_id: user.id
      };

      if (prefill?.id && prefill?.pin_type !== 'hotel') {
        // Edit — in place for the common case (no freeze history on
        // this pin), or a new-row replace if it's ever had one (see
        // edit_pin's own docstring in routes/trips.py for the full
        // reasoning). Either way, a failed edit never loses the
        // original.
        await editPin(prefill.id, tripData);
      } else {
        await pinTrip(tripData);
      }

      navigate('/dashboard');
    } catch (error) {
      // Surface the backend's actual message (e.g. pin limit reached,
      // passenger count too high) instead of a generic failure — these
      // are cases the user can act on, not unexpected bugs. Shown in
      // the shared LimitModal (Aug 19, per Ali's report) rather than a
      // raw browser alert() — same modal Dashboard.js already uses for
      // the urgent-pin-limit case. Extracted into applySubmitError
      // (Sep 20) so handleSubmitHotel below shares the exact same
      // PIN_LIMIT_REACHED handling — see that function's own comment.
      applySubmitError(error);
    }
    setLoading(false);
  };

  const handleSubmitHotel = async () => {
    const budgetValue = parseFloat(hotelForm.budget);
    if (!hotelForm.budget || isNaN(budgetValue) || budgetValue <= 0) {
      setFieldValidationError('Please enter a valid budget.');
      return;
    }

    if (!hotelForm.location) {
      setFieldValidationError('Please enter a location.');
      return;
    }

    // Routed into dateValidationError, not fieldValidationError (Sep
    // 20) — same "Check your dates" modal the flight form's date
    // checks already use, for the same reason: these three are date
    // problems specifically, not generic field problems, and grouping
    // them with the flight form's equivalent checks keeps the title
    // accurate instead of a generic "Check your details" covering
    // something that's actually about dates.
    if (!hotelForm.check_in_date) {
      setDateValidationError('Please select a check-in date.');
      return;
    }

    if (hotelForm.check_in_date < today) {
      setDateValidationError('Check-in date must be in the future.');
      return;
    }

    if (!hotelForm.check_out_date || hotelForm.check_out_date <= hotelForm.check_in_date) {
      setDateValidationError('Check-out date must be after check-in.');
      return;
    }

    // Fixed (Sep 15, per Ali's report — audit finding): `|| 1` only
    // catches NaN or exactly 0, since those are the only falsy number
    // values — a genuine negative like -5 is truthy in JS and sailed
    // straight through. Same isNaN-or-out-of-range pattern already
    // used for budget above, not just a fallback default.
    const numRooms = parseInt(hotelForm.num_rooms, 10);
    const numGuests = parseInt(hotelForm.num_guests, 10);
    if (isNaN(numRooms) || numRooms < 1) {
      setFieldValidationError('Please enter a valid number of rooms.');
      return;
    }
    if (numRooms > MAX_HOTEL_ROOMS) {
      setFieldValidationError(`Maximum ${MAX_HOTEL_ROOMS} rooms per hotel pin.`);
      return;
    }
    if (isNaN(numGuests) || numGuests < 1) {
      setFieldValidationError('Please enter a valid number of guests.');
      return;
    }
    if (numGuests > MAX_HOTEL_GUESTS) {
      setFieldValidationError(`Maximum ${MAX_HOTEL_GUESTS} guests per hotel pin.`);
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await getLocalUser();

      const hotelData = {
        location: hotelForm.location,
        check_in_date: hotelForm.check_in_date,
        check_out_date: hotelForm.check_out_date,
        num_rooms: numRooms,
        num_guests: numGuests,
        budget: budgetValue,
        currency: hotelForm.currency,
        user_id: user.id
      };

      if (prefill?.id && prefill?.pin_type === 'hotel') {
        await editHotelPin(prefill.id, hotelData);
      } else {
        await pinHotel(hotelData);
      }

      navigate('/dashboard');
    } catch (error) {
      // This is also where a free user hitting the hotel paywall shows
      // up — the backend's "Hotels are a Premium feature..." message
      // (from hotels.py's 0-cap check) surfaces here naturally, same as
      // any other pin-limit error. No separate tier check needed on the
      // frontend for this.
      //
      // Was a raw alert(message) with no PIN_LIMIT_REACHED handling at
      // all (Sep 20 cleanup) — now shares applySubmitError with
      // handleSubmitFlight above, so hitting the same max_active_pins
      // cap via a hotel pin gets the identical styled modal and "Buy 3
      // more pins" CTA the flight form already had.
      applySubmitError(error);
    }
    setLoading(false);
  };

  // Extracted (Sep 20) so handleSubmitHotel's catch block — previously
  // a raw alert(message), the last one in this file — gets the exact
  // same PIN_LIMIT_REACHED detection and "Buy 3 more pins" CTA that
  // handleSubmitFlight's catch block already had. That's a real,
  // pre-existing gap on its own, not just a modal swap: hotel pins
  // are capped by the same get_tier_limits().max_active_pins as
  // flight pins (see routes/hotels.py), so hitting that cap while
  // pinning a hotel showed a plain, unstyled error with no path
  // forward, while the identical error on the flight form already
  // offered the real purchase flow.
  const applySubmitError = (error) => {
    const rawMessage = error?.response?.data?.detail || 'Something went wrong. Try again.';
    const pinLimitHit = rawMessage.startsWith('PIN_LIMIT_REACHED:');
    setIsPinLimitError(pinLimitHit);
    setLimitMessage(pinLimitHit ? rawMessage.replace('PIN_LIMIT_REACHED:', '').trim() : rawMessage);
    console.error(error);
  };

  const handleSubmit = () => {
    if (pinType === 'hotel') {
      handleSubmitHotel();
    } else {
      handleSubmitFlight();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://flyatlast.com';
  };

  return (
    <div className="screen screen--wide">
      <PageBackground variant="calm" />
      <LimitModal
        message={limitMessage}
        onClose={() => setLimitMessage(null)}
        // title added (Sep 19, per Ali's report — real bug, caught
        // live from a screenshot: EVERY backend error surfaced here —
        // not just an actual pin-limit error — used LimitModal's
        // default title "Limit reached." A date-format rejection
        // showing "Limit reached" is actively misleading, describing
        // a completely different problem than what actually happened.
        // Tone softened for the pin-limit case specifically (Sep 24,
        // per Ali's report) — the LimitModal shell and its own
        // default title/icon ('Limit reached', ⚠️) are left alone,
        // since Dashboard.js's delete confirmation reuses that same
        // default and this isn't about that flow. This is genuinely
        // good news dressed as a warning: someone hitting this has
        // pinned a lot of real trips. "Limit reached" + a caution
        // icon reads like a wall; this reads like an invitation.
        title={isPinLimitError ? 'Ready for more pins?' : 'Something went wrong'}
        icon={isPinLimitError ? '📌' : '❗'}
        ctaLabel={isPinLimitError ? 'Buy 3 more pins — $3' : undefined}
        onCta={isPinLimitError ? () => { setLimitMessage(null); setShowPurchaseModal(true); } : undefined}
      />
      <LimitModal
        message={dateValidationError}
        onClose={() => setDateValidationError(null)}
        title="Check your dates"
      />
      <LimitModal
        message={fieldValidationError}
        onClose={() => setFieldValidationError(null)}
        title="Check your details"
      />
      <LimitModal
        message={showEarlyLimitModal ? `You're at your ${earlyLimitNotice}-pin limit. You can still fill this out and decide at the end, or buy 3 more pins now.` : null}
        onClose={() => setShowEarlyLimitModal(false)}
        ctaLabel="Buy 3 more pins — $3"
        onCta={() => { setShowEarlyLimitModal(false); setShowPurchaseModal(true); }}
      />
      {showPurchaseModal && userId && (
        <PinBundlePurchaseModal
          userId={userId}
          currency="CAD"
          onSuccess={() => {
            setShowPurchaseModal(false);
            setPurchaseSuccess(true);
          }}
          onCancel={() => setShowPurchaseModal(false)}
        />
      )}
      {purchaseSuccess && (
        // Real modal replacing the old small green banner (Sep 3, per
        // Ali's report) — matches Dashboard.js's equivalent upgrade.
        // Still not an auto-resubmit: the person's form data is still
        // right here exactly as they left it once they dismiss this,
        // so a single "Pin This Trip" click is a smaller, more honest
        // ask than silently resubmitting on their behalf the instant a
        // charge clears.
        <PurchaseSuccessModal
          type="pins"
          onDismiss={() => setPurchaseSuccess(false)}
        />
      )}

      {/* Explicit placeholder color (Aug 17, per Ali's report — the
          accessibility review): browser-default placeholder styling is
          often a fairly low-opacity gray, which was never a real risk
          against the old white input fields but is worth pinning down
          explicitly now that inputs sit on theme.bg rather than pure
          white — guaranteed legible rather than left to whatever the
          browser happens to render. */}
      <style>{`
        input::placeholder { color: ${theme.textMuted}; opacity: 1; }
      `}</style>

      {/* Nav bar (Aug 17, per Ali's report) — this page had none at
          all, the only screen in the app without the consistent logo/
          tagline/sign-out header everyone else has. Also means the
          black "Back to Dashboard" button at the bottom is now
          redundant with this nav's own back link, same pattern already
          fixed on Dashboard, Deal Screen, and Price Freeze.

          Full-bleed fix (Sep 8, per Ali's report — real bug, confirmed
          via screenshot comparison against Dashboard/Payments): the
          simple negative-margin trick below only cancels THIS page's
          own .screen--wide container padding — but that container also
          caps at max-width: 820px (App.css), unlike Dashboard's own
          wrapper, which has no max-width at all. So the old margin
          trick only pulled the bar out to the edges of an 820px box
          sitting in the middle of the real page, not the actual
          viewport — visibly narrower than every other page's nav,
          exactly as the screenshot showed. width:100vw + the
          left:50%/marginLeft:-50vw pair is the standard way to break
          an element out of a centered max-width parent to the true
          viewport edge regardless of how narrow that parent is. */}
      <div style={{ width: '100vw', position: 'relative', left: '50%', marginLeft: '-50vw', marginTop: '-24px', marginBottom: '24px' }}>
        <AppNav current="pin" />
      </div>

      <h2>Pin Your Trip</h2>
      <p>Where do you dream of going?</p>
      {demoTrip && (
        <p style={{ fontSize: '13px', fontWeight: 600, color: theme.textMuted, marginTop: '-8px' }}>
          We filled in your trip from the website. Check the details, then tap Pin This Trip.
        </p>
      )}
      {prefill && (
        <>
          <p style={{ fontSize: '12px', color: '#f97316', marginTop: '-8px' }}>
            Editing {prefill.destination || prefill.location} — pick new dates below
          </p>
          {/* Clarity note (Sep 14, per Ali's report — real confusion,
              caught live via QA screenshots: saving an edit correctly
              starts a fresh tracker and pauses the old one (see
              edit_pin's own docstring in routes/trips.py for the full
              reasoning — it's deliberate, so a failed edit never loses
              the original), but nothing told the user this was about
              to happen, so seeing two cards afterward looked like a
              bug rather than the intended, safer behavior it actually
              is. Applies to both flight and hotel edits — confirmed
              routes/hotels.py's edit_hotel_pin uses the identical
              insert-then-deactivate pattern, not just routes/trips.py. */}
          <p style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '4px', marginBottom: '4px' }}>
            Saving this starts a fresh tracker with your new details — your current one pauses automatically, it isn't lost.
          </p>
        </>
      )}
      {earlyLimitNotice && (
        <div style={{
          background: '#FEF6DE', border: `1px solid ${theme.action}`, padding: '12px 14px',
          fontSize: '13px', color: theme.textPrimary, marginBottom: '18px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
        }}>
          <span>You're at your {earlyLimitNotice}-pin limit — you can still fill this out and decide at the end.</span>
          <span
            onClick={() => setShowPurchaseModal(true)}
            style={{ fontWeight: 700, color: theme.structural, textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Buy 3 more — $3
          </span>
        </div>
      )}

      {/* Progress bar removed entirely (Aug 17, per Ali's report) — it
          was a static visual that never actually tracked real form
          state, which is worse than no indicator at all: same category
          of thing this whole build has been careful to avoid elsewhere
          (fake urgency, unearned claims). */}

      {/* Flight/Hotel toggle (Aug 17, per Ali's report — flagged
          directly: "there is no hotel in there anymore"). The Hotel
          option was already correctly hidden for new pins before this
          change (see the prefill?.pin_type === 'hotel' gate below) —
          what wasn't fixed is that the "Flight" button still rendered
          on its own for every new pin, a single button with nothing to
          toggle to. Now this whole row only renders for the one real
          edge case it exists for: editing an old, already-created hotel
          pin. A brand-new pin skips straight to the form below with no
          toggle row at all. */}
      {prefill?.pin_type === 'hotel' && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
          <button
            type="button"
            disabled
            className={pinType === 'flight' ? 'btn-primary' : ''}
            style={{ flex: 1, padding: '12px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '12px', color: theme.textMuted, cursor: 'default', fontFamily: theme.fontDisplay, fontWeight: 700 }}
          >
            Flight
          </button>
          <button
            type="button"
            disabled
            className={pinType === 'hotel' ? 'btn-primary' : ''}
            style={{ flex: 1, padding: '12px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '12px', color: theme.textMuted, cursor: 'default', fontFamily: theme.fontDisplay, fontWeight: 700 }}
          >
            Hotel
          </button>
        </div>
      )}

      {pinType === 'flight' ? (
        <>
          {/* Side-by-side grid on desktop instead of one narrow
              stacked column — a real user pointed out how much empty
              horizontal space this page was wasting. Falls back to a
              single column automatically on narrow viewports via
              minmax(280px, 1fr), same responsive pattern already used
              elsewhere in the app (e.g. DealScreen.js's 3-column grid). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '14px' }}>
          <div className="pin-form-card" style={{
            borderRadius: '16px', padding: '20px', textAlign: 'left', marginBottom: 0,
            background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)'
          }}>
            <div style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, color: '#4A5F78', letterSpacing: '0.12em', display: 'flex', alignItems: 'center', gap: '6px' }}>WHERE TO</div>

          {/* Departure Airport moved ahead of Destination (Sep 7, per
              external review feedback — real, fair complaint: every
              normal flight-booking form goes From, then To, and this
              had it backwards, which is the kind of small thing that
              makes an otherwise-solid form feel unfamiliar for no
              real reason). */}
          <div style={{ position: 'relative' }}>
          <label style={{ color: theme.textPrimary }}>Departure Airport</label>
          <AirportAutocomplete
            placeholder="e.g. Toronto or YYZ"
            value={{ displayLabel: form.origin_display }}
            onSelect={({ iata_code, displayLabel, iata_country_code }) => {
              // Currency auto-suggestion, departure-first (Sep 15, per
              // Ali's report — real reconsideration: this originally
              // auto-set from the destination's country instead. Ali's
              // own reasoning for flipping it: most people pinning a
              // trip are based near their departure airport, so
              // defaulting to their own local currency is the more
              // useful guess more often, with destination currency as
              // the deliberate override below for "I want to think in
              // what I'll actually spend." Only auto-sets when a
              // mapping actually exists for the country —
              // REGION_TO_CURRENCY is the same curated list already
              // used for the one-time browser-locale guess, not
              // exhaustive, so an unmapped country just leaves
              // whatever currency was already selected untouched.
              // Still a normal editable dropdown afterward — this sets
              // a sensible default, it doesn't lock anything.
              const originCurrency = iata_country_code ? REGION_TO_CURRENCY[iata_country_code.toUpperCase()] : null;
              setForm(prev => ({
                ...prev,
                origin_code: iata_code,
                origin_display: displayLabel,
                origin_country_code: iata_country_code,
                currency: originCurrency || prev.currency,
              }));
            }}
          />

          <label style={{ color: theme.textPrimary, marginTop: '10px', display: 'block' }}>Destination</label>
          <AirportAutocomplete
            placeholder="e.g. London or LHR"
            value={{ displayLabel: form.destination_display || form.destination }}
            onSelect={({ iata_code, displayLabel, iata_country_code }) => {
              // No longer auto-sets currency itself (Sep 15, per Ali's
              // report) — departure does that now, above. Still tracks
              // its own country code so the "Use arrival currency
              // instead" link below has something to offer.
              setForm(prev => ({
                ...prev,
                destination_code: iata_code,
                destination: displayLabel.split(' (')[0],
                destination_display: displayLabel,
                destination_country_code: iata_country_code,
              }));
            }}
          />

          {/* Swap button (Sep 8, per Ali's report) — real, common
              mistake: people fill these in backwards often enough that
              every major flight-search form (Google Flights, Skyscanner,
              Kayak) has this exact control between the two fields.
              Swaps all four paired values together — origin/destination
              code and display, plus destination's own derived plain-text
              field, recomputed the same way onSelect above already does
              (split on " (") rather than just swapping origin_display's
              swapping origin_display's raw value into it, so it can't end up holding something
              like "London (LHR)" instead of the plain "London" every
              other read of form.destination expects. Now also swaps
              the two country codes (Sep 15, per Ali's report — added
              alongside origin_country_code/destination_country_code
              themselves) — without this, swapping fields would leave
              each country code still attached to its old field, so the
              currency suggestion tools above would silently point at
              the wrong airport after a swap. Positioned via
              absolute/top:50% against the relative wrapper spanning both
              fields, landing right on the seam between them regardless
              of which one currently has more content/wrapped text. */}
          <button
            type="button"
            onClick={() => {
              setForm(prev => ({
                ...prev,
                origin_code: prev.destination_code,
                origin_display: prev.destination_display,
                origin_country_code: prev.destination_country_code,
                destination_code: prev.origin_code,
                destination_display: prev.origin_display,
                destination_country_code: prev.origin_country_code,
                destination: (prev.origin_display || '').split(' (')[0],
              }));
            }}
            aria-label="Swap departure and destination"
            style={{
              position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
              width: '30px', height: '30px', borderRadius: '50%', border: `2px solid ${theme.structural}`,
              background: theme.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, zIndex: 2
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={theme.structural} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="17 1 21 5 17 9" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <polyline points="7 23 3 19 7 15" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
          </button>
          </div>

          {form.destination && (
            <p style={{ fontSize: '12px', color: theme.success, fontWeight: 600, margin: '10px 0 0' }}>
              ✨ {form.destination} it is — let's find your deal.
            </p>
          )}
          </div>

          <div className="pin-form-card" style={{
            borderRadius: '16px', padding: '20px', textAlign: 'left', marginBottom: 0,
            background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)'
          }}>
            <div style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, color: '#4A5F78', letterSpacing: '0.12em', display: 'flex', alignItems: 'center', gap: '6px' }}>BUDGET &amp; TRAVELERS</div>

            <label style={{ color: theme.textPrimary }}>Your Budget — total for all passengers</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                name="budget"
                type="number"
                placeholder="e.g. 1500"
                value={form.budget}
                onChange={handleChange}
                style={{ flex: 1, background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textPrimary }}
              />
              <select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                style={{ width: '90px', background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textPrimary }}
              >
                {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* "Use arrival currency" (Sep 15, per Ali's report) —
                flipped direction: departure now drives the automatic
                default above, so this is the manual counterpart
                pointing the other way, toward the destination. Only
                shown when it'd actually change something real: a
                mapping exists for the destination country, AND it
                currently differs from what's selected — otherwise this
                would be a link that does nothing, or states the
                obvious. */}
            {form.destination_country_code && REGION_TO_CURRENCY[form.destination_country_code.toUpperCase()] && REGION_TO_CURRENCY[form.destination_country_code.toUpperCase()] !== form.currency && (
              <p style={{ fontSize: '12.5px', margin: '4px 0 0' }}>
                <span
                  onClick={() => setForm(prev => ({ ...prev, currency: REGION_TO_CURRENCY[prev.destination_country_code.toUpperCase()] }))}
                  style={{ color: theme.action, cursor: 'pointer', fontWeight: 600 }}
                >
                  Use arrival currency ({REGION_TO_CURRENCY[form.destination_country_code.toUpperCase()]}) instead →
                </span>
              </p>
            )}

            {/* Tap-to-add counter instead of typing a number — the
                underlying form.adults value and its validation are
                unchanged, this only replaces how it's entered. */}
            <label style={{ color: theme.textPrimary }}>Adults</label>
            {/* Rewritten Sep 30, per Ali's report — the emoji avatar
                (one 🧑 circle repeated per adult) read as unpolished next
                to an otherwise plain, typography-led form, and didn't
                scale cleanly up to the real 9-passenger max — nine
                repeated circles would crowd the row. A plain numeric
                stepper matches how every other count in this form
                (children, the currency picker) is already presented. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '10px 14px' }}>
              {parseInt(form.adults || 0, 10) > 1 && (
                <button
                  type="button"
                  onClick={removeAdult}
                  style={{ width: '26px', height: '26px', borderRadius: '50%', border: `1.5px solid ${theme.success}`, background: 'transparent', color: theme.success, cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                >
                  −
                </button>
              )}
              <span style={{ fontSize: '15px', fontWeight: 600, color: theme.textPrimary, minWidth: '14px', textAlign: 'center' }}>
                {form.adults}
              </span>
              <button
                type="button"
                onClick={addAdult}
                style={{ width: '26px', height: '26px', borderRadius: '50%', border: `1.5px dashed ${theme.success}`, background: 'transparent', color: theme.success, cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              >
                +
              </button>
              <span style={{ fontSize: '12px', color: theme.textMuted, marginLeft: 'auto' }}>
                adult{parseInt(form.adults, 10) !== 1 ? 's' : ''}
              </span>
            </div>

            <label style={{ color: theme.textPrimary, marginTop: '10px', display: 'block' }}>Children (ages 0-17)</label>
            {childAges.map((age, index) => {
              // Live validation added Sep 30, per Ali's report — real
              // gap, caught live from a screenshot: min/max on a number
              // input only affects the spinner arrows and the browser's
              // own hidden validity state, not what someone can actually
              // type — "18" went in just fine and only got caught later,
              // at submit, by the generic "Check your details" modal.
              // Computed straight from childAges on every render rather
              // than separate state, so it updates the instant they
              // type, not after a blur or a submit attempt.
              const ageOutOfRange = age !== '' && (parseInt(age, 10) < 0 || parseInt(age, 10) > 17);
              return (
              <div key={index} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    max="17"
                    placeholder="Age"
                    value={age}
                    onChange={(e) => updateChildAge(index, e.target.value)}
                    style={{ flex: 1, background: theme.bg, border: `1px solid ${ageOutOfRange ? theme.danger : theme.border}`, color: theme.textPrimary }}
                  />
                  <button
                    type="button"
                    onClick={() => removeChild(index)}
                    style={{ background: theme.inactiveGray, color: theme.textPrimary, border: 'none', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                </div>
                {ageOutOfRange && (
                  <div style={{ fontSize: '12px', color: theme.danger, marginTop: '4px' }}>
                    Must be between 0 and 17.
                  </div>
                )}
              </div>
              );
            })}
            <button
              type="button"
              onClick={addChild}
              style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.structural, borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}
            >
              + Add Child
            </button>

            <p style={{ fontSize: '12px', color: totalPassengers > MAX_TOTAL_PASSENGERS ? theme.danger : theme.textMuted, marginTop: '10px', marginBottom: 0 }}>
              {totalPassengers} passenger{totalPassengers !== 1 ? 's' : ''} total (max {MAX_TOTAL_PASSENGERS})
            </p>
          </div>
          </div>

          <div className="pin-form-card" style={{
            borderRadius: '16px', padding: '20px', marginBottom: '14px', textAlign: 'left',
            background: theme.surface, border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)'
          }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: theme.structural, letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>DATES</div>

            {/* Selected state no longer relies on color alone (Aug 17,
                per Ali's report — the accessibility review): a
                checkmark now appears on the active option too, so
                someone who can't distinguish the two background colors
                still has a clear, non-color signal for which one is
                selected. */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => handleTripTypeChange(false)}
                style={{
                  flex: 1, textAlign: 'center', padding: '10px', borderRadius: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                  border: !isOneWay ? `2px solid ${theme.structural}` : `2px solid ${theme.divider}`,
                  background: !isOneWay ? theme.structural : theme.bg, color: !isOneWay ? '#fff' : theme.textPrimary
                }}
              >
                {!isOneWay && '✓ '}Round-trip
              </button>
              <button
                type="button"
                onClick={() => handleTripTypeChange(true)}
                style={{
                  flex: 1, textAlign: 'center', padding: '10px', borderRadius: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                  border: isOneWay ? `2px solid ${theme.structural}` : `2px solid ${theme.divider}`,
                  background: isOneWay ? theme.structural : theme.bg, color: isOneWay ? '#fff' : theme.textPrimary
                }}
              >
                {isOneWay && '✓ '}➡️ One-way
              </button>
            </div>

          <div style={{ display: 'grid', gridTemplateColumns: isOneWay ? '1fr' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            <div>
              <label style={{ color: theme.textPrimary }}>Departure Date</label>
              <input
                name="departure_date"
                type="date"
                min={today}
                value={form.departure_date}
                onChange={handleChange}
                style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textPrimary }}
              />
            </div>

          {!isOneWay && (
            <div>
              <label style={{ color: theme.textPrimary }}>Return Date</label>
              <input
                name="return_date"
                type="date"
                min={form.departure_date || today}
                value={form.return_date}
                onChange={handleChange}
                style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.textPrimary }}
              />
            </div>
          )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
            <input
              name="date_flexible"
              type="checkbox"
              checked={form.date_flexible}
              onChange={handleChange}
              id="flexible-dates"
              style={{ width: 'auto' }}
            />
            <label htmlFor="flexible-dates" style={{ margin: 0, color: theme.textPrimary }}>
              Flexible dates — also check ±3 days around these dates for a better price
            </label>
          </div>

          {/* Was locked to a plain read-only label whenever editing
              (Sep 20, per Ali's report — real, pre-existing gap: the
              backend already accepted flight_preference on BOTH edit
              paths — the in-place path via search_criteria_changed,
              AND the freeze-history new-row path, which reads it
              straight from the request body exactly like a brand-new
              pin does — but this control never got updated to match,
              so editing it was silently impossible from the UI no
              matter which path a given pin would take. The original
              comment here ("the backend ignores any new value sent
              here anyway") was accurate for the OLD backend behavior,
              just never revisited once that changed). Now renders the
              same real, clickable toggle either way — heading and
              note below just adapt their copy for the edit case,
              since editing carries a real consequence (cache reset +
              a check spent, same as changing route or dates) that
              creating a brand-new pin doesn't. */}
          <div style={{ margin: '4px 0 16px' }}>
            <label style={{ color: theme.textPrimary, display: 'block', marginBottom: '8px' }}>
              What should we track for this trip?{' '}
              <span style={{ opacity: 0.75, fontWeight: 400 }}>
                {!prefill?.id ? "(can't be changed later)" : '(changing this resets tracking, same as a new route or date)'}
              </span>
            </label>
            {/* Selected state adapted for the white-card context (Aug
                17, per Ali's report): the original design used a
                white border to mark the selected option, which relied
                on it standing out against a dark gradient — invisible
                now that the card itself is white. Uses the same
                structural-border + checkmark pattern as the Round-
                trip/One-way toggle above, for the same non-color-only
                reason. */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div
                onClick={() => setForm(f => ({ ...f, flight_preference: 'best_value' }))}
                style={{
                  flex: 1, textAlign: 'center', padding: '12px 10px', borderRadius: '12px', cursor: 'pointer',
                  background: form.flight_preference === 'best_value' ? theme.bg : theme.surface,
                  border: form.flight_preference === 'best_value' ? `2px solid ${theme.structural}` : `2px solid ${theme.divider}`
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>
                  {form.flight_preference === 'best_value' && '✓ '}Best value
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '2px' }}>Cheapest fare, stops OK</div>
              </div>
              <div
                onClick={() => setForm(f => ({ ...f, flight_preference: 'direct' }))}
                style={{
                  flex: 1, textAlign: 'center', padding: '12px 10px', borderRadius: '12px', cursor: 'pointer',
                  background: form.flight_preference === 'direct' ? theme.bg : theme.surface,
                  border: form.flight_preference === 'direct' ? `2px solid ${theme.structural}` : `2px solid ${theme.divider}`
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>
                  {form.flight_preference === 'direct' && '✓ '}Direct only
                </div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '2px' }}>Nonstop, usually pricier</div>
              </div>
            </div>
          </div>
          </div>
        </>
      ) : (
        <>
          <label>Location</label>
          <input
            name="location"
            placeholder="e.g. Oslo, Norway"
            value={hotelForm.location}
            onChange={handleHotelChange}
          />

          <label>Your Budget — total for the stay</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              name="budget"
              type="number"
              placeholder="e.g. 800"
              value={hotelForm.budget}
              onChange={handleHotelChange}
              style={{ flex: 1 }}
            />
            <select
              name="currency"
              value={hotelForm.currency}
              onChange={handleHotelChange}
              style={{ width: '90px' }}
            >
              {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <label>Check-in Date</label>
          <input
            name="check_in_date"
            type="date"
            min={today}
            value={hotelForm.check_in_date}
            onChange={handleHotelChange}
          />

          <label>Check-out Date</label>
          <input
            name="check_out_date"
            type="date"
            min={hotelForm.check_in_date || today}
            value={hotelForm.check_out_date}
            onChange={handleHotelChange}
          />

          <label>Number of Rooms</label>
          <input
            name="num_rooms"
            type="number"
            min="1"
            value={hotelForm.num_rooms}
            onChange={handleHotelChange}
          />

          <label>Number of Guests</label>
          <input
            name="num_guests"
            type="number"
            min="1"
            max={MAX_HOTEL_GUESTS}
            value={hotelForm.num_guests}
            onChange={handleHotelChange}
          />
          <p style={{ fontSize: '12px', color: '#888' }}>
            Max {MAX_HOTEL_GUESTS} guests per hotel pin.
          </p>

          <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
            Hotels are a Premium feature.
          </p>
        </>
      )}

      <button
        className="btn-primary"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (prefill ? 'Saving...' : 'Pinning...') : (prefill ? 'Save Changes' : `Pin This ${pinType === 'hotel' ? 'Hotel' : 'Trip'}`)}
      </button>

      {/* Bottom "Back to Dashboard" button removed (Aug 17, per Ali's
          report) — same redundancy already found and fixed on
          Dashboard, Deal Screen, and Price Freeze: the new nav bar's
          "← Back to Trips" link at the top already covers this. */}
    </div>
  );
}

export default PinTrip;